import { useEffect, useRef } from 'react';
import type { FileRef } from '@/types/file';

/**
 * Backoff schedule in ms (file-handling §14.3.7): quick first look because
 * images and documents finish in ~16s, then widen so a minutes-long video
 * transcode doesn't cost dozens of refetches. The last entry is the cap.
 */
export const FILE_REF_REFETCH_BACKOFF_MS = [3000, 6000, 12000, 30000] as const;

/** True when at least one ref is in a non-terminal state. */
function anyProcessing(refs: readonly (FileRef | null | undefined)[]): boolean {
  return refs.some((ref) => ref?.status === 'processing');
}

/**
 * Identity for a ref set: which refs are processing, and what the current
 * statuses are. Changing this restarts the backoff at 3s — a newly uploaded
 * file deserves the fast first look even if an older one has been polling for
 * a while.
 */
function refsKey(refs: readonly (FileRef | null | undefined)[]): string {
  return refs.map((ref) => (ref ? `${ref.filename}:${ref.status}` : '-')).join('|');
}

interface UseFileRefRefetchOptions {
  /** Keep polling though nothing is processing: a save awaits confirmation. */
  unconfirmed?: boolean;
}

/**
 * Refetch the resource that embeds these FileRefs while any of them is still
 * processing (§14.3.7 — transition discovery rides the parent resource; there
 * is no per-file status endpoint by design).
 *
 * Cheap to sprinkle into any component that renders FileRefs: pass the refs it
 * displays and the query's `refetch`. When nothing is processing, no timer
 * exists. Unmounting cancels the pending refetch.
 *
 * `options.unconfirmed` (#885a-4, invariant I4) keeps the same backoff going
 * while nothing is processing: a save whose re-read came back stale (the ref
 * the save replaced, from a lagging replica) has not been confirmed yet. Feed
 * it from `useUnconfirmedSave().unconfirmed(ref)`. Without the option the hook
 * polls only while something is processing.
 *
 * @example
 * const { data, refetch } = useQuery(...);
 * useFileRefRefetch([data?.avatar, data?.certificate], refetch);
 */
export function useFileRefRefetch(
  refs: readonly (FileRef | null | undefined)[],
  refetch: () => void,
  options: UseFileRefRefetchOptions = {},
): void {
  // Keep the latest refetch callable without restarting the schedule: query
  // hooks hand back a new function identity every render.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const active = anyProcessing(refs) || options.unconfirmed === true;
  const key = refsKey(refs);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    let step = 0;
    let timer: ReturnType<typeof setTimeout>;

    const schedule = () => {
      const delay =
        FILE_REF_REFETCH_BACKOFF_MS[
          Math.min(step, FILE_REF_REFETCH_BACKOFF_MS.length - 1)
        ]!;
      step += 1;
      timer = setTimeout(() => {
        if (cancelled) return;
        refetchRef.current();
        // Keep backing off. If the refetch resolved every file, the ref set
        // changes, `active` goes false, and this effect tears down.
        schedule();
      }, delay);
    };

    schedule();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `key` restarts the backoff whenever the displayed ref set changes.
  }, [active, key]);
}
